/**
 * RadarRenderer — the radar / spider chart lens.
 *
 * Multi-attribute comparison: a small number of series plotted across a shared
 * set of axes arranged radially around a center. Each series becomes a closed
 * polygon; concentric grid rings + radial spokes give the polar scaffold, and
 * axis labels sit just outside the outermost ring (anchored by quadrant so they
 * never overflow the reserved margin).
 *
 * Like every lens it reads a single Theme: dark = luminous polygons on a
 * near-black field; editorial = ink scaffold + saturated polygons with hairline
 * borders on warm off-white. All geometry is computed arithmetically (cos/sin +
 * d3-scale) so the renderer is safe under jsdom — nothing touches getBBox /
 * getComputedTextLength / getBoundingClientRect.
 */

import { scaleLinear } from "d3-scale";
import { select, type Selection } from "d3-selection";

import { THEMES, type Theme } from "../theme.js";
import type {
  LensExample,
  LensRenderer,
  LensRendererOptions,
} from "../lens.js";

export interface RadarSeries {
  name: string;
  /** One value per axis, in axis order. */
  values: number[];
}

export interface RadarData {
  title: string;
  axes: string[];
  series: RadarSeries[];
  /** Outer-ring value. Defaults to the max value present (or 1 if all ≤ 1). */
  max?: number;
}

const SVG_NS = "http://www.w3.org/2000/svg";
const RING_LEVELS = 4;

export class RadarRenderer implements LensRenderer {
  private readonly svg: Selection<SVGSVGElement, unknown, null, undefined>;
  private readonly width: number;
  private readonly height: number;
  private readonly theme: Theme;
  private readonly showLabels: boolean;

  constructor(
    container: SVGSVGElement | HTMLElement,
    options: LensRendererOptions = {},
  ) {
    this.width = options.width ?? 960;
    this.height = options.height ?? 720;
    this.theme =
      typeof options.theme === "object"
        ? options.theme
        : THEMES[options.theme ?? "dark"];
    this.showLabels = options.showLabels ?? true;

    if (container instanceof SVGSVGElement) {
      this.svg = select(container);
    } else {
      const svgEl = document.createElementNS(SVG_NS, "svg");
      container.appendChild(svgEl);
      this.svg = select(svgEl as SVGSVGElement);
    }

    this.svg
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", this.theme.background);
  }

  render(data: unknown): this {
    const d = data as RadarData;
    this.svg.selectAll("*").remove();

    // Background rect so exported SVGs carry the field with them.
    this.svg
      .append("rect")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("fill", this.theme.background);

    const theme = this.theme;
    const isDark = theme.name === "dark";
    const axes = d.axes;
    const n = axes.length;
    if (n < 3) {
      // A radar needs at least 3 axes to be a polygon; render nothing useful.
      return this;
    }

    // Color scaffolding read off the theme.
    const inkStrong = isDark ? "rgba(255,255,255,0.85)" : "rgba(26,26,26,0.9)";
    const gridStroke = isDark ? "rgba(255,255,255,0.14)" : "rgba(26,26,26,0.12)";
    const gridStrongStroke = isDark
      ? "rgba(255,255,255,0.28)"
      : "rgba(26,26,26,0.22)";

    // ── geometry ────────────────────────────────────────────────────────
    // Reserve generous margin for axis labels + legend; chart fills the rest.
    const pad = 48;
    const labelMargin = 92; // room for axis text rings
    const cx = this.width / 2;
    const cy = this.height / 2 + 8; // nudge down to leave room for the title
    const radius = Math.max(
      40,
      Math.min(
        this.width / 2 - pad - labelMargin,
        this.height / 2 - pad - labelMargin,
      ),
    );

    // Normalize by max value (default = largest value present, or 1 if all 0..1).
    let maxV = d.max;
    if (maxV === undefined) {
      let m = -Infinity;
      for (const s of d.series) for (const v of s.values) m = Math.max(m, v);
      maxV = m <= 1 ? 1 : m;
    }
    if (!Number.isFinite(maxV) || maxV <= 0) maxV = 1;

    const rScale = scaleLinear().domain([0, maxV]).range([0, radius]);

    // Angle for axis i: start at top (−90°), go clockwise.
    const angleOf = (i: number) => (i / n) * 2 * Math.PI - Math.PI / 2;
    const pointAt = (i: number, value: number): [number, number] => {
      const a = angleOf(i);
      const r = rScale(Math.max(0, Math.min(maxV, value)));
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    };
    const ringPoint = (i: number, level: number): [number, number] => {
      const a = angleOf(i);
      const r = (radius * level) / RING_LEVELS;
      return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
    };

    const content = this.svg.append("g").attr("class", "content");

    // ── grid rings (concentric polygons) ────────────────────────────────
    const grid = content.append("g").attr("class", "grid");
    for (let level = 1; level <= RING_LEVELS; level++) {
      const pts: string[] = [];
      for (let i = 0; i < n; i++) {
        const [x, y] = ringPoint(i, level);
        pts.push(`${round(x)},${round(y)}`);
      }
      grid
        .append("polygon")
        .attr("points", pts.join(" "))
        .attr("fill", "none")
        .attr("stroke", level === RING_LEVELS ? gridStrongStroke : gridStroke)
        .attr("stroke-width", level === RING_LEVELS ? 1.4 : 1);
    }

    // ── radial spokes ───────────────────────────────────────────────────
    const spokes = content.append("g").attr("class", "spokes");
    for (let i = 0; i < n; i++) {
      const [x, y] = ringPoint(i, RING_LEVELS);
      spokes
        .append("line")
        .attr("x1", round(cx))
        .attr("y1", round(cy))
        .attr("x2", round(x))
        .attr("y2", round(y))
        .attr("stroke", gridStroke)
        .attr("stroke-width", 1);
    }

    // ── ring value tick labels (along the top spoke) ────────────────────
    if (this.showLabels) {
      const ticks = content.append("g").attr("class", "ring-ticks");
      for (let level = 1; level <= RING_LEVELS; level++) {
        const val = (maxV * level) / RING_LEVELS;
        const r = (radius * level) / RING_LEVELS;
        ticks
          .append("text")
          .attr("x", round(cx + 4))
          .attr("y", round(cy - r - 2))
          .attr("font-family", theme.label.valueFontFamily ?? theme.label.fontFamily)
          .attr("font-size", theme.label.fontSize - 0.5)
          .attr("fill", theme.label.valueColor)
          .attr("text-anchor", "start")
          .attr("paint-order", "stroke")
          .attr("stroke", theme.background)
          .attr("stroke-width", 2.5)
          .attr("stroke-linejoin", "round")
          .text(formatTick(val, maxV));
      }
    }

    // ── series polygons ─────────────────────────────────────────────────
    const colorOf = (i: number): string =>
      i < theme.maxColoredClusters
        ? theme.palette[i % theme.palette.length]
        : theme.neutralCluster;

    const seriesG = content.append("g").attr("class", "series");
    d.series.forEach((s, si) => {
      const color = colorOf(si);
      const pts: string[] = [];
      const verts: [number, number][] = [];
      for (let i = 0; i < n; i++) {
        const v = s.values[i] ?? 0;
        const [x, y] = pointAt(i, v);
        pts.push(`${round(x)},${round(y)}`);
        verts.push([x, y]);
      }

      const g = seriesG.append("g").attr("class", "series-poly");
      g.append("polygon")
        .attr("points", pts.join(" "))
        .attr("fill", color)
        .attr("fill-opacity", isDark ? 0.18 : 0.22)
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .attr("stroke-linejoin", "round");

      // Vertex dots.
      for (const [x, y] of verts) {
        const dot = g
          .append("circle")
          .attr("cx", round(x))
          .attr("cy", round(y))
          .attr("r", 3.2)
          .attr("fill", color)
          .attr("fill-opacity", isDark ? 0.95 : 0.98);
        if (theme.node.stroke) {
          dot
            .attr("stroke", theme.node.stroke)
            .attr("stroke-width", theme.node.strokeWidth ?? 1);
        }
      }
    });

    // ── axis labels (outside the outer ring, anchored by quadrant) ──────
    if (this.showLabels) {
      const labelsG = content.append("g").attr("class", "axis-labels");
      const labelGap = 16;
      for (let i = 0; i < n; i++) {
        const a = angleOf(i);
        const lx = cx + (radius + labelGap) * Math.cos(a);
        const ly = cy + (radius + labelGap) * Math.sin(a);
        const cosA = Math.cos(a);
        // Anchor by horizontal quadrant; near-vertical axes get centered.
        let anchor: "start" | "middle" | "end";
        if (Math.abs(cosA) < 0.2) anchor = "middle";
        else if (cosA > 0) anchor = "start";
        else anchor = "end";
        // Vertical baseline nudge so top/bottom labels clear the ring.
        const sinA = Math.sin(a);
        const dy = sinA < -0.2 ? -2 : sinA > 0.2 ? 9 : 3;

        labelsG
          .append("text")
          .attr("x", round(lx))
          .attr("y", round(ly + dy))
          .attr("font-family", theme.label.fontFamily)
          .attr("font-size", theme.label.fontSize + 1.5)
          .attr("fill", inkStrong)
          .attr("text-anchor", anchor)
          .attr("paint-order", "stroke")
          .attr("stroke", theme.background)
          .attr("stroke-width", 2.5)
          .attr("stroke-linejoin", "round")
          .text(axes[i]);
      }
    }

    // ── title ───────────────────────────────────────────────────────────
    if (this.showLabels && d.title) {
      content
        .append("text")
        .attr("x", round(pad))
        .attr("y", round(pad - 12))
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", 18)
        .attr("font-weight", 600)
        .attr("fill", inkStrong)
        .attr("text-anchor", "start")
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 3)
        .attr("stroke-linejoin", "round")
        .text(d.title);
    }

    // ── legend (top-right corner) ───────────────────────────────────────
    if (this.showLabels) {
      const legend = content.append("g").attr("class", "legend");
      const swatch = 12;
      const rowH = 20;
      const fs = theme.label.fontSize + 1;
      // Right-align the block; estimate width arithmetically (no DOM measuring).
      let maxLabelChars = 0;
      for (const s of d.series) maxLabelChars = Math.max(maxLabelChars, s.name.length);
      const blockW = swatch + 8 + maxLabelChars * fs * 0.6;
      const lx = this.width - pad - blockW;
      const ly = pad - 4;

      d.series.forEach((s, si) => {
        const color = colorOf(si);
        const row = legend.append("g").attr(
          "transform",
          `translate(${round(lx)},${round(ly + si * rowH)})`,
        );
        const sw = row
          .append("rect")
          .attr("x", 0)
          .attr("y", 0)
          .attr("width", swatch)
          .attr("height", swatch)
          .attr("rx", 2)
          .attr("fill", color)
          .attr("fill-opacity", isDark ? 0.85 : 0.95);
        if (theme.node.stroke) {
          sw
            .attr("stroke", theme.node.stroke)
            .attr("stroke-width", theme.node.strokeWidth ?? 1);
        }
        row
          .append("text")
          .attr("x", swatch + 8)
          .attr("y", swatch - 2)
          .attr("font-family", theme.label.fontFamily)
          .attr("font-size", fs)
          .attr("fill", inkStrong)
          .attr("text-anchor", "start")
          .attr("paint-order", "stroke")
          .attr("stroke", theme.background)
          .attr("stroke-width", 2.5)
          .attr("stroke-linejoin", "round")
          .text(s.name);
      });
    }

    return this;
  }

  settle(_ticks?: number): this {
    // Static polar layout — nothing to settle.
    return this;
  }

  exportSVG(): string {
    const node = this.svg.node();
    if (!node) return "";
    const clone = node.cloneNode(true) as SVGSVGElement;
    return new XMLSerializer().serializeToString(clone);
  }

  destroy(): void {
    this.svg.selectAll("*").remove();
  }
}

// ── pure helpers ────────────────────────────────────────────────────────────

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Compact tick label: integers stay integers; sub-1 maxima render as %. */
function formatTick(val: number, maxV: number): string {
  if (maxV <= 1) return `${Math.round(val * 100)}`;
  if (Number.isInteger(val)) return String(val);
  return val.toFixed(1);
}

// ── examples ──────────────────────────────────────────────────────────────

export const RADAR_EXAMPLES: LensExample[] = [
  {
    id: "radar-agent-capability",
    title: "Agent Capability Profiles",
    blurb:
      "Three Totem Protocol agents scored across the dimensions that decide which one gets a job.",
    highlights: ["multi-axis", "agent routing", "3 series"],
    lens: "radar",
    build: (): RadarData => ({
      title: "Agent Capability Profiles",
      axes: ["Reasoning", "Memory", "Tooling", "Speed", "Cost-Eff.", "Autonomy"],
      max: 10,
      series: [
        { name: "Orchestrator", values: [9, 8, 7, 4, 5, 9] },
        { name: "Link Processor", values: [5, 4, 9, 9, 8, 6] },
        { name: "Insight Synthesizer", values: [8, 9, 5, 5, 6, 7] },
      ],
    }),
  },
  {
    id: "radar-persona-comparison",
    title: "Persona Comparison",
    blurb:
      "How the Jonny personas differ across the traits that shape their voice and output.",
    highlights: ["persona", "voice profile", "overlap"],
    lens: "radar",
    build: (): RadarData => ({
      title: "Persona Comparison",
      axes: [
        "Technical Depth",
        "Synthesis",
        "Directness",
        "Pedagogy",
        "Narrative",
        "Rigor",
      ],
      max: 10,
      series: [
        { name: "Technical", values: [10, 6, 9, 5, 3, 9] },
        { name: "SenseCollective", values: [6, 10, 6, 7, 9, 7] },
        { name: "Educational", values: [7, 7, 7, 10, 8, 6] },
      ],
    }),
  },
  {
    id: "radar-gap-scoring",
    title: "Gap Scoring",
    blurb:
      "Current state of the content flywheel versus the target across the rubric dimensions.",
    highlights: ["target vs current", "rubric", "gap"],
    lens: "radar",
    build: (): RadarData => ({
      title: "Content Flywheel — Gap Scoring",
      axes: [
        "Source Capture",
        "Classification",
        "Synthesis",
        "Distribution",
        "Cadence",
        "Feedback Loop",
      ],
      max: 100,
      series: [
        { name: "Target", values: [90, 85, 90, 80, 85, 80] },
        { name: "Current", values: [70, 55, 45, 35, 50, 30] },
      ],
    }),
  },
];
